import { initializeApp } from 'firebase/app';

// Falha rápido se envs obrigatórias faltarem (exceto no modo emulador que usa valores dummy)
if (import.meta.env.VITE_USE_EMULATOR !== 'true') {
  const REQUIRED_VARS = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
  ] as const;
  const missing = REQUIRED_VARS.filter((k) => !import.meta.env[k]);
  if (missing.length > 0) {
    throw new Error(
      `[Lura] Missing required env vars: ${missing.join(', ')}. ` +
      'Check your .env file or CI secrets.',
    );
  }
}
import {
  type Auth,
  getAuth,
  initializeAuth,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { isNativeApp } from '../platform/native';
import { resolveFirestoreCacheMode } from './firestoreCache';
import { bootMark } from '../boot/bootTrace';
import {
  type Firestore,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  persistentSingleTabManager,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initAppCheck } from './appCheck';

/**
 * Resolve `authDomain` dinamicamente.
 *
 * **Motivação (gotcha 2026-05-20):** Default Firebase é
 * `lura-e2b70.firebaseapp.com`, mas em preview channels o app roda em
 * `lura-e2b70--<channel>-<hash>.web.app`. signInWithRedirect (forçado em
 * mobile) faz round-trip pelo handler `<authDomain>/__/auth/handler` e
 * tenta setar cookie de sessão. Safari iOS (ITP) e Chrome Mobile (Privacy
 * Sandbox) bloqueiam cookies cross-domain entre `*.web.app` e
 * `*.firebaseapp.com` → sessão perdida no redirect de volta → SDK
 * retorna `auth/popup-closed-by-user` → UI mostra "Login cancelado".
 *
 * **Fix:** usar o próprio `window.location.hostname` como authDomain
 * quando estamos em produção (não localhost, não emulator). Firebase
 * Hosting expõe `/__/auth/handler` em **qualquer domínio do projeto**
 * (incluindo preview channels + custom domains), desde que esteja
 * autorizado em Authentication → Settings → Authorized domains.
 *
 * Casos cobertos:
 *  - localhost (dev): usa env var (lura-e2b70.firebaseapp.com)
 *  - emulator: usa env var
 *  - preview channel: usa lura-e2b70--<channel>-<hash>.web.app
 *  - live (lura-e2b70.web.app): usa o próprio hostname
 *  - custom domain futuro: usa o custom (precisa autorizar no Console)
 *
 * ⚠️ **BUILD NATIVO É EXCEÇÃO, e não é detalhe.** Esta heurística nasceu em
 * 20/05/2026, quando o app só existia na web; o app Capacitor nasceu em
 * 12/08 e ninguém a revisitou. Dentro do app, `window.location.hostname` é o
 * host do SERVIDOR LOCAL do Capacitor — hoje `localhost`, e até 13/08/2026 o
 * domínio real `uselivra.com` (BUG-116). Em nenhum dos dois casos ele é um
 * domínio de Hosting: é o bundle embarcado. Devolvê-lo como `authDomain`
 * apontaria o SDK para `/__/auth/iframe` num host que não serve o handler.
 *
 * Com o hostname antigo isso era pior do que "não funciona", porque cada
 * plataforma falhava de um jeito diferente e nenhum deles dizia o próprio nome:
 *
 *  - **Android** (`androidScheme: https`): o WebView servia `https://uselivra.com/*`
 *    do bundle LOCAL, então `/__/auth/iframe` devolvia o index.html do próprio
 *    app em vez do handler. O iframe nunca respondia ao ping.
 *  - **iOS**: o `iosScheme: "https"` é INVÁLIDO e o Capacitor o descarta em
 *    silêncio (`WKWebView.handlesURLScheme("https")` é `true`, então
 *    `CAPInstanceDescriptor.normalize()` volta ao default `capacitor`). A origem
 *    real era `capacitor://uselivra.com`, e aí `https://uselivra.com` de fato ia
 *    à internet — mas o iframe passava a ser cross-origin em relação a um
 *    esquema que jamais estará nos Authorized Domains do Firebase.
 *
 * Nos dois casos o app nativo não ganha nada com isso: ele **não usa** popup nem
 * redirect (o Google recusa OAuth em WebView — ver `authService`), então o
 * authDomain só precisa ser um domínio válido do projeto. Usar o do `.env` tira
 * uma dependência de rede do caminho crítico do boot.
 *
 * 📌 **A troca de hostname de 13/08 NÃO mexe nesta função, e isso é o ponto.**
 * O early-return de `isNativeApp()` já tinha tirado `location.hostname` do
 * caminho nativo no build 23; por isso a mudança de origem passa por aqui sem
 * efeito. O que a troca exige de auth é nada: sem `popupRedirectResolver` o SDK
 * não consulta Authorized Domains, e o `signInWithCredential` fala com o
 * `identitytoolkit` por REST — medido em 13/08, a API key do projeto não tem
 * restrição de referrer, então `capacitor://localhost` e `https://localhost`
 * respondem igual a `https://uselivra.com`.
 */
function resolveAuthDomain(): string {
  const fallback = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string;
  if (typeof window === 'undefined') return fallback;
  if (import.meta.env.VITE_USE_EMULATOR === 'true') return fallback;
  if (isNativeApp()) return fallback;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return fallback;
  return host;
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: resolveAuthDomain(),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

export const firebaseApp = app;

// App Check — atestação de origem (anti-abuso). Logo após `initializeApp` para
// que o token esteja registrado antes das primeiras chamadas de Firestore e
// callable. Fire-and-forget e à prova de boot: sem chave, é no-op. Rollout e
// blueprint em `docs/systems/app-check.md`.
initAppCheck(app);

/**
 * Auth do app — e no build NATIVO ele nasce **sem `popupRedirectResolver`**.
 *
 * ── O que o resolver custa, medido no SDK instalado ─────────────────────────
 * `getAuth(app)` injeta `browserPopupRedirectResolver`. Com ele presente,
 * `AuthImpl.initializeCurrentUser` executa:
 *
 *     if (popupRedirectResolver && this.config.authDomain) {
 *       …
 *       const result = await this.tryRedirectSignIn(popupRedirectResolver);
 *     }
 *
 * e `tryRedirectSignIn` → `_openIframe` → carrega `https://apis.google.com/js/api.js`
 * e embute `https://{authDomain}/__/auth/iframe`. Esse `await` está **dentro**
 * de `_initializationPromise`, e `registerStateListener` — que é quem serve o
 * `onAuthStateChanged` — só chama o callback depois dela:
 *
 *     const promise = this._isInitialized ? Promise.resolve() : this._initializationPromise;
 *     promise.then(() => cb(this.currentUser));
 *
 * Ou seja: **o primeiro `onAuthStateChanged` do app fica atrás de duas idas à
 * rede de terceiros.** No AuthContext é ele quem tira `isLoading` de `true`, e
 * `isLoading` é o que o `RootEntry` observa para decidir entre spinner e app.
 * Rede ruim, iframe bloqueado ou authDomain que não responde = spinner que não
 * termina. Os únicos tetos são internos do SDK (ping 15s, gapi 30–60s em
 * mobile), o que na prática é indistinguível de travado.
 *
 * ── Por que o nativo pode simplesmente não ter isso ─────────────────────────
 * Popup e redirect não são usados no app: o Google recusa OAuth em WebView
 * (`disallowed_useragent`), e o login nativo entrega a credencial ao
 * `signInWithCredential`, que não passa pelo resolver. Sem resolver, o `if`
 * acima é pulado inteiro e o primeiro `onAuthStateChanged` depende só da
 * persistência local. Zero rede antes da primeira tela.
 *
 * ⚠️ **Consequência real:** com o Auth assim, `signInWithPopup`,
 * `signInWithRedirect` e `getRedirectResult` lançam `auth/argument-error`
 * (`_withDefaultResolver` faz `_assert(auth._popupRedirectResolver, …)`). É por
 * isso que `consumeGoogleRedirectResult` tem early-return no nativo — as duas
 * peças são uma só, não mexa numa sem a outra.
 */
function createAuth(): Auth {
  if (!isNativeApp()) return getAuth(app);
  return initializeAuth(app, {
    // Mesma cadeia do `getAuth`, menos o resolver. A ordem importa: o SDK usa
    // a primeira que estiver disponível no ambiente.
    persistence: [
      indexedDBLocalPersistence,
      browserLocalPersistence,
      browserSessionPersistence,
    ],
  });
}

export const auth = createAuth();

// Idioma dos e-mails transacionais do Auth (verificação, redefinição de senha).
// Sem isto o SDK não manda `X-Firebase-Locale` e o Firebase usa o template em
// INGLÊS: "Verify your email for lura-e2b70", vindo de um domínio que a pessoa
// nunca viu — assunto em outra língua é um dos sinais que o filtro de spam
// soma (BUG-264). Só vale quando o template do Console não foi customizado; se
// foi, o idioma escolhido lá prevalece e esta linha é inofensiva.
auth.languageCode = 'pt-BR';

bootMark(
  'firebase-init',
  'info',
  `authDomain=${firebaseConfig.authDomain} native=${isNativeApp()}`,
);

if (import.meta.env.VITE_USE_EMULATOR === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
}

let firestoreInstance: Firestore;
try {
  firestoreInstance = initializeFirestore(app, {
    // O modo de cache e decidido por PLATAFORMA. O porque completo (e o
    // travamento que isto conserta) esta em `firestoreCache.ts`: no app
    // nativo so existe UMA aba, e a eleicao de lider do modo multi-aba
    // escreve no IndexedDB exatamente quando o WebKit congela o processo,
    // travando a AsyncQueue do Firestore de forma permanente.
    localCache:
      resolveFirestoreCacheMode(isNativeApp()) === 'single-owner'
        ? persistentLocalCache({
            tabManager: persistentSingleTabManager({ forceOwnership: true }),
          })
        : persistentLocalCache({
            tabManager: persistentMultipleTabManager(),
          }),
  });
} catch (error) {
  console.warn('[Lura] Firestore local cache indisponível; usando modo padrão.', error);
  firestoreInstance = getFirestore(app);
}

if (import.meta.env.VITE_USE_EMULATOR === 'true') {
  connectFirestoreEmulator(firestoreInstance, '127.0.0.1', 8080);
}

export const db = firestoreInstance;

// Cloud Functions — region us-central1 (default). Quando emulator está ativo,
// callable httpsCallable e onCall apontam pra 127.0.0.1:5001 em vez de
// us-central1-lura-e2b70.cloudfunctions.net. Necessário pra unifiedSearch,
// getAiRecommendations etc rodarem local na suite visual.
export const functions = getFunctions(app, 'us-central1');

if (import.meta.env.VITE_USE_EMULATOR === 'true') {
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}